import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { clearItemsWithCountCache } from 'shared/ui/ItemsWithCount/ItemsWithCount'

const FAST_JUMP_ROWS = 5

export type IUseVirtualRowsParams = {
  enabled: boolean
  count: number
  getItemKey: (index: number) => string | number
  scrollElement: HTMLElement | null
  estimateRowHeight: number
  dynamicRowHeight?: boolean
  overscan: number
  rowsKey: string
  windowRows: boolean
  skeletonWhileScrolling: boolean
}

export const useVirtualRows = ({
  enabled,
  count,
  getItemKey,
  scrollElement,
  estimateRowHeight,
  dynamicRowHeight,
  overscan,
  rowsKey,
  windowRows,
  skeletonWhileScrolling,
}: IUseVirtualRowsParams) => {
  const getScrollElement = useCallback(
    () => (enabled ? scrollElement : null),
    [enabled, scrollElement]
  )

  const rowVirtualizer = useVirtualizer<HTMLElement, HTMLTableRowElement>({
    enabled: enabled && !!scrollElement,
    count: enabled ? count : 0,
    getScrollElement,
    estimateSize: () => estimateRowHeight,
    overscan,
    getItemKey,
    useFlushSync: false,
  })

  useEffect(() => {
    if (!enabled || !scrollElement) return
    clearItemsWithCountCache()
    scrollElement.dispatchEvent(new Event('scroll'))
  }, [rowsKey, enabled, scrollElement])

  const rowRefCache = useMemo(
    () => new Map<number, (node: HTMLTableRowElement | null) => void>(),
    [rowVirtualizer, dynamicRowHeight]
  )
  const getRowRef = useCallback(
    (index: number) => {
      let rowRef = rowRefCache.get(index)

      if (!rowRef) {
        rowRef = (node) => {
          if (node) node.dataset.index = String(index)
          if (dynamicRowHeight) rowVirtualizer.measureElement(node)
        }
        rowRefCache.set(index, rowRef)
      }

      return rowRef
    },
    [rowRefCache, rowVirtualizer, dynamicRowHeight]
  )

  const virtualRows = enabled ? rowVirtualizer.getVirtualItems() : []
  const paddingTop = virtualRows.length ? virtualRows[0].start : 0
  const paddingBottom = virtualRows.length
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0

  const windowKey = `${rowsKey}:${count}`
  const prevWindowRef = useRef<Set<number>>(new Set())
  const windowKeyRef = useRef(windowKey)

  if (windowKeyRef.current !== windowKey) {
    windowKeyRef.current = windowKey
    prevWindowRef.current = new Set()
  }

  const prevScrollOffsetRef = useRef<number | null>(null)
  const scrolledSinceLastFrame = rowVirtualizer.scrollOffset !== prevScrollOffsetRef.current

  useEffect(() => {
    if (!windowRows) return
    prevWindowRef.current = new Set(virtualRows.map((virtualRow) => virtualRow.index))
    prevScrollOffsetRef.current = rowVirtualizer.scrollOffset
  })

  const enteringCount =
    windowRows && skeletonWhileScrolling
      ? virtualRows.reduce(
          (sum, virtualRow) => sum + (prevWindowRef.current.has(virtualRow.index) ? 0 : 1),
          0
        )
      : 0
  const skeletonFastJump =
    prevWindowRef.current.size > 0 && scrolledSinceLastFrame && enteringCount > FAST_JUMP_ROWS
  const shouldRenderSkeleton = (index: number) =>
    skeletonFastJump && !prevWindowRef.current.has(index)

  return {
    virtualRows,
    paddingTop,
    paddingBottom,
    getRowRef,
    shouldRenderSkeleton,
  }
}
